import { useState, useEffect, useCallback, useRef } from 'react';
import locationService, { UserLocation } from '../services/LocationService';

interface UseLocationModalProps {
   userId: string;
   isAuthenticated: boolean;
}

interface UseLocationModalReturn {
   showModal: boolean;
   openModal: () => void;
   closeModal: () => void;
   skipModal: () => void;
   onLocationSaved: (location: UserLocation) => void;
   hasLocation: boolean;
}

const MODAL_RESHOW_DELAY = 5 * 60 * 1000; // 5 minutes in milliseconds
const STORAGE_KEY = 'locationModal';

interface LocationModalState {
   lastClosed: number;
   hasBeenShownThisSession: boolean;
   hasBeenSkipped: boolean;
}

export const useLocationModal = ({
   userId,
   isAuthenticated,
}: UseLocationModalProps): UseLocationModalReturn => {
   const [showModal, setShowModal] = useState(false);
   const [hasLocation, setHasLocation] = useState(false);
   const timerRef = useRef<NodeJS.Timeout | null>(null);
   const hasCheckedOnLogin = useRef(false);

   console.log('📍 useLocationModal hook initialized with:', { userId, isAuthenticated });

   // Get modal state from localStorage
   const getModalState = useCallback((): LocationModalState => {
      try {
         const stored = localStorage.getItem(`${STORAGE_KEY}_${userId}`);
         if (stored) {
            return JSON.parse(stored);
         }
      } catch (error) {
         console.error('Error reading location modal state from localStorage:', error);
      }
      return {
         lastClosed: 0,
         hasBeenShownThisSession: false,
         hasBeenSkipped: false,
      };
   }, [userId]);

   // Save modal state to localStorage
   const saveModalState = useCallback(
      (state: LocationModalState) => {
         try {
            localStorage.setItem(`${STORAGE_KEY}_${userId}`, JSON.stringify(state));
         } catch (error) {
            console.error('Error saving location modal state to localStorage:', error);
         }
      },
      [userId]
   );

   // Check if user has location
   const checkUserLocation = useCallback(async () => {
      console.log('📍 checkUserLocation called with:', { userId, isAuthenticated });

      if (!userId || !isAuthenticated) {
         console.log('📍 Skipping location check - missing userId or not authenticated');
         return;
      }

      try {
         console.log('📍 Fetching user location from Firestore...');
         const location = await locationService.getUserLocation(userId);
         const hasUserLocation = !!location;

         console.log('📍 Location check result:', {
            location: location ? 'found' : 'not found',
            hasUserLocation,
            locationData: location
               ? {
                    address: location.address,
                    city: location.city,
                    country: location.country,
                 }
               : null,
         });

         setHasLocation(hasUserLocation);

         // If user doesn't have location, consider showing the modal
         if (!hasUserLocation) {
            console.log('📍 User has no location, checking modal state...');
            const modalState = getModalState();
            const now = Date.now();
            const timeSinceLastClosed = now - modalState.lastClosed;

            console.log('📍 Location modal state:', {
               hasBeenSkipped: modalState.hasBeenSkipped,
               hasBeenShownThisSession: modalState.hasBeenShownThisSession,
               timeSinceLastClosed: timeSinceLastClosed,
               timeSinceLastClosedMinutes: Math.round(timeSinceLastClosed / (1000 * 60)),
               MODAL_RESHOW_DELAY_MINUTES: MODAL_RESHOW_DELAY / (1000 * 60),
               condition1: !modalState.hasBeenSkipped,
               condition2: !modalState.hasBeenShownThisSession,
               condition3: timeSinceLastClosed >= MODAL_RESHOW_DELAY,
               shouldShow:
                  !modalState.hasBeenSkipped &&
                  (!modalState.hasBeenShownThisSession ||
                     timeSinceLastClosed >= MODAL_RESHOW_DELAY),
            });

            // Show modal if:
            // 1. It hasn't been shown this session AND hasn't been permanently skipped, OR
            // 2. It's been more than 5 minutes since it was last closed AND hasn't been permanently skipped
            if (
               !modalState.hasBeenSkipped &&
               (!modalState.hasBeenShownThisSession || timeSinceLastClosed >= MODAL_RESHOW_DELAY)
            ) {
               console.log('📍 Showing location modal!');
               setShowModal(true);
            } else {
               console.log('📍 NOT showing location modal - conditions not met:', {
                  hasBeenSkipped: modalState.hasBeenSkipped,
                  hasBeenShownThisSession: modalState.hasBeenShownThisSession,
                  timeSinceLastClosedMinutes: Math.round(timeSinceLastClosed / (1000 * 60)),
                  needsToWaitMinutes: modalState.hasBeenSkipped
                     ? 'permanently skipped'
                     : Math.round((MODAL_RESHOW_DELAY - timeSinceLastClosed) / (1000 * 60)),
               });
            }
         } else {
            console.log('📍 User already has location, not showing modal');
         }
      } catch (error) {
         console.error('📍 Error checking user location:', error);
      }
   }, [userId, isAuthenticated, getModalState]);

   // Check location when user logs in
   useEffect(() => {
      console.log('📍 Location check useEffect triggered:', {
         isAuthenticated,
         userId,
         hasCheckedOnLogin: hasCheckedOnLogin.current,
      });

      if (isAuthenticated && userId && !hasCheckedOnLogin.current) {
         hasCheckedOnLogin.current = true;
         console.log('📍 Checking user location on login...');
         // Small delay to ensure the user has fully logged in
         setTimeout(() => {
            console.log('📍 Timeout completed, calling checkUserLocation...');
            checkUserLocation();
         }, 3000); // Delay a bit more than invitations to avoid modal conflicts
      }
   }, [isAuthenticated, userId, checkUserLocation]);

   // Also check immediately on first mount if user is already authenticated
   useEffect(() => {
      console.log('📍 Immediate check useEffect triggered:', { isAuthenticated, userId });
      if (isAuthenticated && userId) {
         console.log('📍 Immediate check for user location on mount...');
         checkUserLocation();
      }
   }, [isAuthenticated, userId, checkUserLocation]);

   // Add global functions for testing
   useEffect(() => {
      if (typeof window !== 'undefined') {
         (window as any).clearLocationModalState = () => {
            if (userId) {
               localStorage.removeItem(`${STORAGE_KEY}_${userId}`);
               console.log('📍 Location modal state cleared for user:', userId);
               checkUserLocation();
            }
         };

         (window as any).clearUserLocation = async () => {
            if (userId) {
               try {
                  const { deleteDoc, doc } = await import('firebase/firestore');
                  const { db } = await import('../firebase/config');
                  await deleteDoc(doc(db, 'locations', userId));
                  console.log('📍 User location deleted from Firestore for user:', userId);
                  localStorage.removeItem(`${STORAGE_KEY}_${userId}`);
                  checkUserLocation();
               } catch (error) {
                  console.error('📍 Error deleting user location:', error);
               }
            }
         };

         (window as any).forceShowLocationModal = () => {
            console.log('📍 Forcing location modal to show...');
            setShowModal(true);
         };

         (window as any).getLocationModalState = () => {
            const state = getModalState();
            console.log('📍 Current location modal state:', state);
            return state;
         };
      }
   }, [userId, checkUserLocation]);

   // Set up periodic checks for location
   useEffect(() => {
      if (!isAuthenticated || !userId) {
         return;
      }

      // Check every 5 minutes for location
      const interval = setInterval(
         () => {
            checkUserLocation();
         },
         5 * 60 * 1000
      );

      return () => clearInterval(interval);
   }, [isAuthenticated, userId, checkUserLocation]);

   // Open modal manually
   const openModal = useCallback(() => {
      setShowModal(true);
   }, []);

   // Close modal and set up timer for re-showing
   const closeModal = useCallback(() => {
      console.log('📍 Closing location modal, setting 5-minute timer...');
      setShowModal(false);

      const modalState = getModalState();
      const newState: LocationModalState = {
         ...modalState,
         lastClosed: Date.now(),
         hasBeenShownThisSession: true,
      };
      saveModalState(newState);

      // Clear any existing timer
      if (timerRef.current) {
         clearTimeout(timerRef.current);
      }

      // Set up timer to re-show modal if user still doesn't have location
      timerRef.current = setTimeout(() => {
         console.log('📍 5 minutes passed, checking user location again...');
         checkUserLocation();
      }, MODAL_RESHOW_DELAY);
   }, [getModalState, saveModalState, checkUserLocation]);

   // Skip modal permanently
   const skipModal = useCallback(() => {
      setShowModal(false);

      const modalState = getModalState();
      const newState: LocationModalState = {
         ...modalState,
         lastClosed: Date.now(),
         hasBeenShownThisSession: true,
         hasBeenSkipped: true, // Mark as permanently skipped
      };
      saveModalState(newState);

      // Clear any existing timer
      if (timerRef.current) {
         clearTimeout(timerRef.current);
      }
   }, [getModalState, saveModalState]);

   // Handle location saved
   const onLocationSaved = useCallback((location: UserLocation) => {
      setHasLocation(true);
      setShowModal(false);

      // Clear any existing timer since user now has location
      if (timerRef.current) {
         clearTimeout(timerRef.current);
      }
   }, []);

   // Clean up timer on unmount
   useEffect(() => {
      return () => {
         if (timerRef.current) {
            clearTimeout(timerRef.current);
         }
      };
   }, []);

   // Reset session state when user changes
   useEffect(() => {
      hasCheckedOnLogin.current = false;
      setShowModal(false);
      setHasLocation(false);

      if (timerRef.current) {
         clearTimeout(timerRef.current);
         timerRef.current = null;
      }
   }, [userId]);

   return {
      showModal,
      openModal,
      closeModal,
      skipModal,
      onLocationSaved,
      hasLocation,
   };
};
